// Capacidades de la app según el PERFIL del usuario.
//
// ÚNICA fuente de verdad de "qué se ve y qué no". Las pantallas NO deben
// consultar `claims` para decidir su UI: consumen estos flags, que tienen nombre
// semántico. Así, cuando entre un país nuevo o cambie una regla de negocio, se
// toca SOLO este fichero y no hay que perseguir condiciones por media app.
//
// Perfiles:
//   - 'classic'      → controladores de oficina (España, Argentina…). Su
//                      comportamiento NO debe cambiar nunca por estos cambios.
//   - 'self-managed' → controladores que crean su propia planning
//                      (PK/EG/BG/RO/GR/IT, ver NEW_FLOW_COUNTRIES en claims.ts).
//   - 'supplier'     → proveedores (rol Supplier, 2026-09-10): ven y rellenan
//                      SOLO las cargas de sus proveedores (el servidor acota
//                      por token). Mismos campos que un controlador, ninguno
//                      obligatorio, sin crear plannings, sin borrar, sin VGM.

import type { UserClaims } from './claims';
import { getPreferences } from './storage';

export type UserProfile = 'classic' | 'self-managed' | 'supplier';

export interface FeatureFlags {
  /** VGM (Verified Gross Mass): trámite de los puertos españoles (Portic). */
  vgm: boolean;
  /** Campo "Lot number" del detalle de carga. */
  lotNumber: boolean;
  /**
   * Campo de texto "location" DEL DETALLE (dónde está el lote).
   * OJO: no confundir con la ubicación GPS de New Planning (fase 5), que es un
   * concepto distinto y tiene su propio flag `gpsLocation`.
   */
  detailLocation: boolean;
  /**
   * Matrícula de camión y placa de remolque, con su botón de OCR.
   * No hay flag aparte para ese OCR: solo existe para leer las matrículas, así
   * que desaparece con ellas. El OCR de contenedor y precinto NO se ve afectado
   * (son campos distintos, útiles para todos los perfiles).
   */
  truckPlate: boolean;
  /** Poder borrar cargas. */
  deleteCargo: boolean;
  /**
   * El botón (+) de añadir detalle se puede configurar desde Ajustes.
   * Para self-managed NO se ofrece el interruptor: ese (+) es su ÚNICA vía de
   * añadir detalles a una carga (DetailPage), así que se deja siempre activo —
   * apagarlo los dejaría sin forma de rellenar la carga que acaban de crear.
   */
  addCargoConfigurable: boolean;
  /** Puede crear su propia planning (pantalla New Planning). */
  createPlanning: boolean;
  /** Campo Booking en la pantalla de New Planning. */
  planningBooking: boolean;
  /** Si además de mostrarse, el Booking es obligatorio para poder crear. */
  planningBookingRequired: boolean;
  /**
   * Usa el catálogo de productos SELF-MANAGED (tabla propia, filtrado por los
   * países del usuario y con alta por el propio controlador) en lugar de la
   * maestra clásica de productos.
   */
  selfManagedProductCatalog: boolean;
  /** Supplier como texto libre en lugar del selector de fábrica. */
  supplierFreeText: boolean;
  /** Campo de ubicación GPS en New Planning. */
  gpsLocation: boolean;
  /** Idiomas más allá de ES/EN. */
  extendedLanguages: boolean;
  /**
   * El número de contenedor es obligatorio al guardar un detalle. Para el
   * proveedor no lo es: rellena lo que tenga y el controlador remata.
   */
  containerNumberRequired: boolean;
  /**
   * Enseña en la ficha del contenedor quién y cuándo lo rellenó un usuario
   * proveedor. Para todos salvo el propio proveedor, que ya sabe lo que hizo.
   */
  supplierStamp: boolean;
  /**
   * Filtro rápido "Controlador" en la lista de cargas (oct 2026). Solo el admin
   * ve las cargas de todos y quién lleva cada una; un controlador solo ve las
   * suyas. Los filtros por fábrica y transportista son de todos (salen solos
   * cuando el día tiene más de un valor).
   */
  filterByController: boolean;
  /**
   * Sección "Documento de transporte (PDF)" en la ficha del contenedor
   * (2026-10-23): subir, ver, sustituir y quitar el DDT. Es del proveedor, que
   * es quien tiene el papel, y del admin. Un controlador no la ve.
   */
  deliveryNote: boolean;
}

export function getUserProfile(claims: UserClaims): UserProfile {
  if (claims.isSupplier) return 'supplier';
  return claims.isSelfManaged ? 'self-managed' : 'classic';
}

export function getFeatures(claims: UserClaims): FeatureFlags {
  const profile = getUserProfile(claims);
  if (profile === 'supplier') return getSupplierFeatures();
  const selfManaged = profile === 'self-managed';
  // Un admin también crea plannings del flujo nuevo, aunque no tenga el claim
  // self-managed-country. Se limita a ESA pantalla a propósito: marcarlo como
  // self-managed a secas le quitaría matrículas, VGM y borrado en el flujo
  // clásico, que sigue usando. Las capacidades de abajo con este flag solo
  // viven dentro de New Planning.
  const canCreatePlanning = selfManaged || claims.isAdmin;
  return {
    vgm: !selfManaged,
    lotNumber: !selfManaged,
    detailLocation: !selfManaged,
    truckPlate: !selfManaged,
    // Para self-managed, borrar cargas SOLO lo puede un admin. En el flujo
    // clásico se respeta el comportamiento actual (preferencia protegida por
    // PIN en Ajustes): no le quitamos a un controlador de oficina una capacidad
    // que hoy tiene.
    deleteCargo: selfManaged ? claims.isAdmin : true,
    addCargoConfigurable: !selfManaged,
    createPlanning: canCreatePlanning,
    // El booking se ocultó al self-managed cuando se rediseñó la pantalla, pero
    // el controlador sí lo conoce y necesita registrarlo: vuelve para los dos
    // flujos. Es texto libre, sin formato impuesto.
    planningBooking: true,
    // Exigirlo solo donde siempre se exigió. En self-managed se pide pero no se
    // obliga: un controlador que aún no tenga el booking a mano no puede
    // quedarse sin poder crear la planning por eso.
    planningBookingRequired: !canCreatePlanning,
    // Las tres siguientes SOLO se leen dentro de New Planning, que es una
    // pantalla del flujo nuevo: quien entre en ella debe ver el catálogo por
    // país, el proveedor libre y el GPS, sea controlador o admin. Si el admin
    // usara aquí la maestra clásica, crearía plannings sin producto del
    // catálogo, que es justo lo que la app no sabe mostrar.
    selfManagedProductCatalog: canCreatePlanning,
    supplierFreeText: canCreatePlanning,
    gpsLocation: canCreatePlanning,
    extendedLanguages: selfManaged,
    containerNumberRequired: true,
    supplierStamp: true,
    filterByController: claims.isAdmin,
    deliveryNote: claims.isAdmin,
  };
}

// El proveedor ve la ficha del contenedor como un controlador y rellena lo
// que tenga (nada obligatorio). Lo que NO hace: crear plannings, borrar
// cargas, mandar el VGM (trámite del controlador), configurar el (+) ni el lote
// y la ubicación: ese interruptor de Ajustes es de las fábricas internas de
// Aldahra y a un proveedor no le aplica (2026-09-12, lo pidió Daniel).
function getSupplierFeatures(): FeatureFlags {
  return {
    vgm: false,
    lotNumber: false,
    detailLocation: false,
    truckPlate: true,
    deleteCargo: false,
    addCargoConfigurable: false,
    createPlanning: false,
    planningBooking: true,
    planningBookingRequired: false,
    selfManagedProductCatalog: false,
    supplierFreeText: false,
    gpsLocation: false,
    extendedLanguages: true,
    containerNumberRequired: false,
    supplierStamp: false,
    filterByController: false,
    deliveryNote: true,
  };
}

export type EffectivePreferences = ReturnType<typeof getPreferences>;

// Preferencias REALES que debe aplicar la app: las que guardó el usuario,
// anuladas por las capacidades de su perfil.
//
// Se pasa siempre por aquí (en vez de leer getPreferences() directamente) para
// que un valor heredado en localStorage —por ejemplo un DELETE_ENABLED=true que
// el usuario activó antes de pasar a self-managed— no reactive algo que su
// perfil ya no permite. Ocultar el interruptor en Ajustes no basta.
export function getEffectivePreferences(claims: UserClaims): EffectivePreferences {
  const stored = getPreferences();
  const features = getFeatures(claims);
  return {
    ...stored,
    vgmEnabled: features.vgm && stored.vgmEnabled,
    locationEnabled: features.detailLocation && stored.locationEnabled,
    deleteEnabled: features.deleteCargo && stored.deleteEnabled,
    // Sin interruptor en Ajustes → SIEMPRE activo. Se fuerza a true (en vez de
    // respetar lo guardado) para que un `false` heredado no deje al usuario sin
    // el único botón que tiene para añadir detalles a una carga.
    addCargoEnabled: features.addCargoConfigurable ? stored.addCargoEnabled : true,
  };
}
